// Default background images and utilities for trips, events, and albums

export const DEFAULT_GRADIENTS = {
   trip: [
      'linear-gradient(135deg, #01949A 0%, #004369 100%)', // Primary: Teal to Navy
      'linear-gradient(135deg, #D38E2C 0%, #BC0B25 100%)', // Warm: Dark Yellow to Red
      'linear-gradient(135deg, #2BB3B8 0%, #007075 100%)', // Light Teal variations
      'linear-gradient(135deg, #1A5F85 0%, #002A42 100%)', // Navy variations
      'linear-gradient(135deg, #E5DDC8 0%, #D4C8A8 100%)', // Beige variations
      'linear-gradient(135deg, #01949A 0%, #D38E2C 100%)', // Teal to Yellow
      'linear-gradient(135deg, #004369 0%, #BC0B25 100%)', // Navy to Red
      'linear-gradient(135deg, #10B981 0%, #059669 100%)', // Success greens
   ],
   event: [
      'linear-gradient(135deg, #01949A 0%, #004369 100%)', // Primary: Teal to Navy
      'linear-gradient(135deg, #D38E2C 0%, #BC0B25 100%)', // Warm: Dark Yellow to Red
      'linear-gradient(135deg, #2BB3B8 0%, #007075 100%)', // Light Teal variations
      'linear-gradient(135deg, #1A5F85 0%, #002A42 100%)', // Navy variations
      'linear-gradient(135deg, #E5DDC8 0%, #D4C8A8 100%)', // Beige variations
      'linear-gradient(135deg, #01949A 0%, #D38E2C 100%)', // Teal to Yellow
      'linear-gradient(135deg, #004369 0%, #BC0B25 100%)', // Navy to Red
      'linear-gradient(135deg, #10B981 0%, #059669 100%)', // Success greens
   ],
   album: [
      'linear-gradient(135deg, #01949A 0%, #004369 100%)', // Primary: Teal to Navy
      'linear-gradient(135deg, #D38E2C 0%, #BC0B25 100%)', // Warm: Dark Yellow to Red
      'linear-gradient(135deg, #2BB3B8 0%, #007075 100%)', // Light Teal variations
      'linear-gradient(135deg, #1A5F85 0%, #002A42 100%)', // Navy variations
      'linear-gradient(135deg, #E5DDC8 0%, #D4C8A8 100%)', // Beige variations
      'linear-gradient(135deg, #01949A 0%, #D38E2C 100%)', // Teal to Yellow
      'linear-gradient(135deg, #004369 0%, #BC0B25 100%)', // Navy to Red
      'linear-gradient(135deg, #10B981 0%, #059669 100%)', // Success greens
   ],
};

// Generate a consistent gradient based on ID
export const getDefaultGradient = (type: 'trip' | 'event' | 'album', id: string): string => {
   const gradients = DEFAULT_GRADIENTS[type];
   // Use a simple hash of the ID to get consistent gradient
   let hash = 0;
   for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
   }
   const index = Math.abs(hash) % gradients.length;
   return gradients[index];
};

// Create a data URL for a gradient background
export const createGradientDataUrl = (
   gradient: string,
   width: number = 400,
   height: number = 200
): string => {
   const canvas = document.createElement('canvas');
   canvas.width = width;
   canvas.height = height;
   const ctx = canvas.getContext('2d');

   if (!ctx) return '';

   // Parse gradient string to create canvas gradient
   // For simplicity, we'll create a basic linear gradient
   const gradientMatch = gradient.match(/linear-gradient\(([^)]+)\)/);
   if (gradientMatch) {
      const parts = gradientMatch[1].split(',').map((s) => s.trim());
      const angle = parts[0];
      const colors = parts.slice(1);

      // Create linear gradient
      const canvasGradient = ctx.createLinearGradient(0, 0, width, height);

      colors.forEach((color, index) => {
         const colorMatch = color.match(/(#[0-9a-fA-F]{6}|rgb\([^)]+\))/);
         if (colorMatch) {
            const stop = index / (colors.length - 1);
            canvasGradient.addColorStop(stop, colorMatch[1]);
         }
      });

      ctx.fillStyle = canvasGradient;
      ctx.fillRect(0, 0, width, height);
   }

   return canvas.toDataURL();
};

// Get default background style for CSS
export const getDefaultBackgroundStyle = (
   type: 'trip' | 'event' | 'album',
   id: string,
   coverUrl?: string | null
) => {
   console.log(`🎨 getDefaultBackgroundStyle for ${type} ${id}:`, {
      coverUrl,
      hasValidUrl: !!(coverUrl && coverUrl.trim() !== ''),
   });

   // Check if we have a valid cover URL (not null, undefined, or empty string)
   if (coverUrl && coverUrl.trim() !== '') {
      console.log(`✅ Using image for ${type} ${id}`);
      return {
         backgroundImage: `url("${coverUrl}")`,
         backgroundSize: 'cover',
         backgroundPosition: 'center',
         backgroundRepeat: 'no-repeat',
         backgroundColor: 'transparent',
      };
   }

   console.log(`🌈 Using gradient for ${type} ${id}`);
   return {
      backgroundImage: 'none', // Clear any background image
      background: getDefaultGradient(type, id),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
   };
};

// Default images for different types
export const DEFAULT_IMAGES = {
   trip: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
   event: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
   album: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
};

// Get fallback image URL
export const getFallbackImage = (type: 'trip' | 'event' | 'album'): string => {
   return DEFAULT_IMAGES[type];
};

// Component helper for default background with icon overlay
export const getDefaultBackgroundWithIcon = (
   type: 'trip' | 'event' | 'album',
   id: string,
   title?: string
) => ({
   background: getDefaultGradient(type, id),
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'center',
   flexDirection: 'column',
   color: 'white',
   textAlign: 'center',
   padding: '20px',
   position: 'relative',
   '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.3)',
      zIndex: 1,
   },
   '& > *': {
      position: 'relative',
      zIndex: 2,
   },
});
